// Liste „Sonstige“ (/admin/kontakte) im Widget-Stil (Handoff Sonstige Kontakte §1, Screens 01–04).
// Funktionen wie bisher in KontakteListe (lockedQuelle 'kontakt'): Suche, Auch Inaktive, Neuer Kontakt, Klick → Detailseite.
// Neu nur der Umschalter Kacheln/Liste (lokal gemerkt).
import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddressBook, CircleNotch, UserPlus, WarningCircle } from '@phosphor-icons/react';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import { GlasTooltipSchicht } from '../../../../components/ui/GlasTooltip';
import { leseAnsicht, merkeAnsicht, type SonstigeAnsicht } from '../../../../lib/kontakte/ansicht';
import { kontakteAnzahl } from '../../../../lib/kontakte/sonstige';
import { useSonstigeListe } from '../../../../lib/kontakte/useSonstigeListe';
import MieterErstellenFormular from '../../mieter/MieterErstellenFormular';
import { SonstigeKachel, SonstigeZeile } from './Eintraege';
import { KontaktFormularSheet } from './KontaktFormularSheet';
import { AnsichtSegment, InaktivSchalter, NeuMenue, SuchFeld, type NeuEintrag } from './Steuerzeile';

function Hinweis({ icon, titel, text, fehler = false }: { icon: ReactNode; titel: string; text?: string; fehler?: boolean }) {
  return (
    <div role={fehler ? 'alert' : 'status'} className="sk-glas sk-rise flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '56px 20px', gap: 12 }}>
      <span aria-hidden className="flex items-center justify-center"
        style={{ width: 56, height: 56, borderRadius: 18, background: fehler ? 'var(--bad-soft)' : 'var(--fill2)', color: fehler ? 'var(--bad)' : 'var(--ink3)' }}>
        {icon}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: fehler ? 'var(--bad)' : 'var(--ink2)' }}>{titel}</span>
      {text && <span style={{ fontSize: 12, color: 'var(--ink3)', maxWidth: 480, overflowWrap: 'anywhere' }}>{text}</span>}
    </div>
  );
}

export default function SonstigeListeSeite() {
  const navigate = useNavigate();
  const { openForm, closeForm } = useIslandActions();
  const [suche, setSuche] = useState('');
  const [nurAktiv, setNurAktiv] = useState(true);
  const [neuOffen, setNeuOffen] = useState(false);
  const [ansicht, setAnsicht] = useState<SonstigeAnsicht>(() => leseAnsicht());
  const { zustand, neuLaden } = useSonstigeListe(suche, nurAktiv);

  const ansichtWaehlen = (a: SonstigeAnsicht) => { setAnsicht(a); merkeAnsicht(a); };
  const erledigt = useCallback(() => { closeForm(); neuLaden(); }, [closeForm, neuLaden]);
  const oeffne = (id: string) => navigate('/admin/kontakte/kontakt/' + id);

  const neuEintraege: NeuEintrag[] = [
    { key: 'mieter', label: 'Mieter', onClick: () => openForm(<MieterErstellenFormular onDone={erledigt} />, 'Neuer Mieter', 720,
      { icon: UserPlus, farbe: 'gruen', badge: 'plus', pflichtHinweis: true }) },
    // neuer Eigentümer im neuen Formular des Eigentümer-Bereichs (CSV-Import, Objekte & Einheiten)
    { key: 'eigentuemer', label: 'Eigentümer', onClick: () => navigate('/admin/eigentuemer?neu=1') },
    // neuer Dienstleister in der Drehkarte des Dienstleister-Bereichs (Gewerke, Konditionen, Nachweise)
    { key: 'dienstleister', label: 'Dienstleister', onClick: () => navigate('/admin/dienstleister?neu=1') },
    // Sonstiger Kontakt im Glas-Sheet (Handoff §3); danach Liste neu laden wie bisher
    { key: 'kontakt', label: 'Sonstiger Kontakt', onClick: () => setNeuOffen(true) },
  ];

  const kontakte = zustand.status === 'ok' ? zustand.kontakte : [];

  return (
    <div className="sk-flaeche flex flex-col" style={{ gap: 14 }}>
      <header className="flex flex-wrap items-end justify-between" style={{ gap: 12, padding: '4px 4px 0' }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.02em' }}>Sonstige</span>
          {/* Entscheidung 19.10.2026: Bereich heißt „Kontakte“ (Prototyp: Überzeile Kontakte, Titel Sonstige) */}
          <h1 className="m-0" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.025em' }}>Kontakte</h1>
        </div>
        {zustand.status === 'ok' && (
          <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>{kontakteAnzahl(kontakte.length)}</span>
        )}
      </header>

      <div className="relative flex flex-wrap items-center" style={{ gap: 10, zIndex: 5 }}>
        <SuchFeld wert={suche} onChange={setSuche} />
        <AnsichtSegment wert={ansicht} onChange={ansichtWaehlen} />
        <InaktivSchalter an={!nurAktiv} onChange={an => setNurAktiv(!an)} />
        <NeuMenue eintraege={neuEintraege} />
      </div>

      {zustand.status === 'laedt' ? (
        <Hinweis icon={<CircleNotch weight="bold" size={26} className="animate-spin" />} titel="Lade Kontakte…" />
      ) : zustand.status === 'fehler' ? (
        <Hinweis fehler icon={<WarningCircle weight="fill" size={28} />} titel="Fehler beim Abrufen der Daten" text={zustand.meldung} />
      ) : kontakte.length === 0 ? (
        <Hinweis icon={<AddressBook weight="duotone" size={28} />} titel="Keine Kontakte gefunden" />
      ) : ansicht === 'kacheln' ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: 12 }}>
          {kontakte.map((k, i) => (
            <SonstigeKachel key={k.quelle_id} kontakt={k} firma={zustand.firmen.has(k.quelle_id)} nr={i} onOeffnen={() => oeffne(k.quelle_id)} />
          ))}
        </div>
      ) : (
        <div className="sk-glas sk-rise flex flex-col" style={{ borderRadius: 26, padding: 6, gap: 2, animationDuration: '.45s' }}>
          {kontakte.map((k, i) => (
            <SonstigeZeile key={k.quelle_id} kontakt={k} firma={zustand.firmen.has(k.quelle_id)} nr={i} onOeffnen={() => oeffne(k.quelle_id)} />
          ))}
        </div>
      )}

      {neuOffen && <KontaktFormularSheet kontakt={null} onSchliessen={() => setNeuOffen(false)} onGespeichert={() => { setNeuOffen(false); neuLaden(); }} />}
      <GlasTooltipSchicht />
    </div>
  );
}
